// src/pages/ventes/Reglements.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Table, Row, Col, Form, Button, Badge,
    Spinner, Alert, Tooltip, OverlayTrigger, Modal, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

export default function Reglements() {
    const navigate = useNavigate();
    const [reglements, setReglements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [filtreMode, setFiltreMode] = useState('tous');
    const [sortConfig, setSortConfig] = useState({ key: 'date_reglement', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmVariant, setConfirmVariant] = useState('primary');
    const [isProcessing, setIsProcessing] = useState(false);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });
    const showSuccess = (msg) => showToast('success', 'Succès', msg);
    const showError   = (msg) => showToast('error', 'Erreur', msg);

    useEffect(() => {
        fetchReglements();
        fetchStats();
    }, [filtreStatut, filtreMode]);

    const openConfirmModal = (id, title, message, variant = 'primary', action) => {
        setConfirmId(id);
        setConfirmTitle(title);
        setConfirmMessage(message);
        setConfirmVariant(variant);
        setConfirmAction(() => action);
        setShowConfirmModal(true);
    };

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchReglements = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/reglements.php`;
            const params = new URLSearchParams();
            if (filtreStatut !== 'tous') params.append('statut', filtreStatut);
            if (filtreMode !== 'tous') params.append('mode_reglement', filtreMode);
            if (params.toString()) url += '?' + params.toString();

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setReglements(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleValider = async (id) => {
        openConfirmModal(
            id,
            "Valider le règlement",
            "Êtes-vous sûr de vouloir valider ce règlement ? Le solde du client et de la facture sera mis à jour.",
            "success",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(`${API_BASE_URL}/reglements.php`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ action: 'valider', id: confirmId })
                    });
                    const result = await response.json();
                    if (result.success) {
                        setShowConfirmModal(false);
                        showSuccess("Règlement validé avec succès");
                        fetchReglements();
                        fetchStats();
                    } else {
                        showError(result.message || "Erreur lors de la validation");
                    }
                } catch (err) {
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const handleSupprimer = async (id) => {
        openConfirmModal(
            id,
            "Confirmer la suppression",
            "Êtes-vous sûr de vouloir supprimer ce règlement ? Cette action est irréversible.",
            "danger",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(`${API_BASE_URL}/reglements.php?id=${confirmId}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const result = await response.json();
                    if (result.success) {
                        setShowConfirmModal(false);
                        showSuccess("Règlement supprimé avec succès");
                        fetchReglements();
                        fetchStats();
                    } else {
                        showError(result.message || "Erreur lors de la suppression");
                    }
                } catch (err) {
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStatutConfig = (statut) => {
        const configs = {
            en_attente: { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'En attente', icon: 'ri-time-line' },
            valide:     { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Validé',     icon: 'ri-checkbox-circle-line' },
            rejete:     { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Rejeté',     icon: 'ri-close-circle-line' }
        };
        return configs[statut] || configs.en_attente;
    };

    const getStatutBadge = (statut) => {
        const config = getStatutConfig(statut);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const getModeIcon = (mode) => {
        const icons = {
            especes: 'ri-money-euro-circle-line',
            cheque: 'ri-bank-card-line',
            virement: 'ri-bank-line',
            carte_bancaire: 'ri-smartphone-line',
            traite: 'ri-file-copy-line'
        };
        return icons[mode] || 'ri-bank-card-line';
    };

    const getModeLabel = (mode) => {
        const labels = {
            especes: 'Espèces',
            cheque: 'Chèque',
            virement: 'Virement',
            carte_bancaire: 'Carte bancaire',
            traite: 'Traite'
        };
        return labels[mode] || mode;
    };

    /* ============================================================
       FILTER / SORT / PAGINATION
       ============================================================ */
    const filteredReglements = useMemo(() => {
        return reglements
            .filter(r =>
                r.numero_reglement?.toLowerCase().includes(filterText.toLowerCase()) ||
                r.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                r.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                r.reference?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_reglement') {
                    return sortConfig.direction === 'asc'
                        ? new Date(a.date_reglement) - new Date(b.date_reglement)
                        : new Date(b.date_reglement) - new Date(a.date_reglement);
                }
                if (sortConfig.key === 'montant') {
                    return sortConfig.direction === 'asc' ? a.montant - b.montant : b.montant - a.montant;
                }
                return 0;
            });
    }, [reglements, filterText, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredReglements.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredReglements.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }
        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children, className = '' }) => (
        <th className={`sortable-th-charte ${className}`} onClick={() => handleSort(field)}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ icon, onClick, tooltip, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
    if (loading && !reglements.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-bank-card-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des règlements
                        </h5>
                        <p className="text-muted small">Récupération des données en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error && !reglements.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchReglements} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bank-card-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Règlements</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-list-line"></i>
                                        {filteredReglements.length} règlement(s)
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-filter-line"></i>
                                        {filtreStatut === 'tous' ? 'Tous les statuts' : filtreStatut}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchReglements} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate('/ventes/NouveauReglement')}>
                                <i className="ri-add-line"></i> Nouveau règlement
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="6">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-bank-card-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Ce mois</span>
                                        <h3 className="kpi-value-charte">{stats.mois?.nb_reglements || 0}</h3>
                                        <span className="kpi-unit-charte">{formatCurrency(stats.mois?.total_montant || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="6">
                                <div className="kpi-charte kpi-danger">
                                    <div className="kpi-icon-charte"><i className="ri-error-warning-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Impayés</span>
                                        <h3 className="kpi-value-charte">{stats.impayes?.nb_factures || 0}</h3>
                                        <span className="kpi-unit-charte">{formatCurrency(stats.impayes?.total_impaye || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher règlement, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="tous">Tous les statuts</option>
                                        <option value="en_attente">En attente</option>
                                        <option value="valide">Validés</option>
                                        <option value="rejete">Rejetés</option>
                                    </Form.Select>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={filtreMode}
                                        onChange={(e) => setFiltreMode(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="tous">Tous les modes</option>
                                        <option value="especes">Espèces</option>
                                        <option value="cheque">Chèque</option>
                                        <option value="virement">Virement</option>
                                        <option value="carte_bancaire">Carte bancaire</option>
                                        <option value="traite">Traite</option>
                                    </Form.Select>
                                </Col>
                                <Col md="2" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i>
                                        {filteredReglements.length} règlement(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-bank-card-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des règlements</span>
                                    <span className="badge-header-light">{filteredReglements.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">N° Règlement</th>
                                            <SortableHeader field="date_reglement">Date</SortableHeader>
                                            <th>Client</th>
                                            <th>Facture</th>
                                            <th>Mode</th>
                                            <SortableHeader field="montant">Montant</SortableHeader>
                                            <th className="text-center">Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((reg) => (
                                                <tr key={reg.id_reglement}>
                                                    <td className="ps-4">
                                                        <Link
                                                            to={`/ventes/reglements/${reg.id_reglement}`}
                                                            className="table-link-charte"
                                                        >
                                                            {reg.numero_reglement}
                                                        </Link>
                                                    </td>
                                                    <td className="text-muted">{formatDate(reg.date_reglement)}</td>
                                                    <td style={{ minWidth: '140px' }}>
                                                        <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{reg.client_nom}</div>
                                                        <small className="text-muted">{reg.code_client}</small>
                                                    </td>
                                                    <td>
                                                        {reg.numero_facture ? (
                                                            <Link to={`/ventes/facturesDetails/${reg.id_facture}`} className="table-link-charte" style={{ fontSize: '0.8rem' }}>
                                                                {reg.numero_facture}
                                                            </Link>
                                                        ) : <span className="text-muted">—</span>}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="mode-icon-charte">
                                                                <i className={getModeIcon(reg.mode_reglement)}></i>
                                                            </div>
                                                            <div>
                                                                <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                    {getModeLabel(reg.mode_reglement)}
                                                                </span>
                                                                {reg.reference && (
                                                                    <div><small className="text-muted">{reg.reference}</small></div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                                        {formatCurrency(reg.montant)}
                                                    </td>
                                                    <td className="text-center">{getStatutBadge(reg.statut)}</td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <ActionButton
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/ventes/reglements/${reg.id_reglement}`)}
                                                                tooltip="Voir détails"
                                                            />
                                                            {reg.statut === 'en_attente' && (
                                                                <ActionButton
                                                                    icon="ri-check-double-line"
                                                                    onClick={() => handleValider(reg.id_reglement)}
                                                                    tooltip="Valider"
                                                                    color={COLORS.success}
                                                                />
                                                            )}
                                                            <ActionButton
                                                                icon="ri-delete-bin-line"
                                                                onClick={() => handleSupprimer(reg.id_reglement)}
                                                                tooltip="Supprimer"
                                                                color={COLORS.danger}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-bank-card-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucun règlement trouvé</h6>
                                                        <p className="text-muted small mb-3">
                                                            Commencez par saisir un nouveau règlement
                                                        </p>
                                                        <Button
                                                            onClick={() => navigate('/ventes/NouveauReglement')}
                                                            className="btn-modern-primary"
                                                            size="sm"
                                                        >
                                                            <i className="ri-add-line me-1"></i>Nouveau règlement
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-wrapper-charte">
                                    <div className="pagination-info-charte">
                                        Affichage de <strong>{indexOfFirstItem + 1}</strong> à <strong>{Math.min(indexOfLastItem, filteredReglements.length)}</strong> sur <strong>{filteredReglements.length}</strong> règlement(s)
                                    </div>
                                    <Pagination className="pagination-charte mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL CONFIRMATION ===== */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered className="modal-charte">
                <Modal.Header
                    closeButton
                    className="modal-header-charte"
                    style={{
                        background: confirmVariant === 'success'
                            ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                            : confirmVariant === 'danger'
                            ? `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`
                            : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon">
                            <i className={
                                confirmVariant === 'success' ? 'ri-check-double-line' :
                                confirmVariant === 'danger' ? 'ri-delete-bin-line' :
                                'ri-information-line'
                            }></i>
                        </div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">{confirmTitle}</Modal.Title>
                            <p className="text-white-50 mb-0 small">Confirmation requise</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    <p className="text-muted mb-4" dangerouslySetInnerHTML={{ __html: confirmMessage }}></p>
                    {confirmVariant === 'danger' && (
                        <Alert className="alert-error-charte mb-0">
                            <div className="d-flex align-items-center">
                                <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                                <div className="ms-3">
                                    <strong>Attention</strong>
                                    <p className="mb-0 small">Cette action est irréversible</p>
                                </div>
                            </div>
                        </Alert>
                    )}
                    {confirmVariant === 'success' && (
                        <Alert className="alert-info-charte mb-0">
                            <div className="d-flex align-items-start gap-3">
                                <i className="ri-information-line fs-4"></i>
                                <div>
                                    <small className="d-block">✓ Le règlement sera validé</small>
                                    <small className="d-block">✓ Le solde du client sera mis à jour</small>
                                </div>
                            </div>
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowConfirmModal(false)} className="btn-modern-outline" disabled={isProcessing}>
                        Annuler
                    </Button>
                    <Button
                        onClick={() => confirmAction(confirmId)}
                        disabled={isProcessing}
                        className={
                            confirmVariant === 'success' ? 'btn-modern-success' :
                            confirmVariant === 'danger' ? 'btn-modern-danger' :
                            'btn-modern-primary'
                        }
                    >
                        {isProcessing ? (
                            <><Spinner size="sm" className="me-2" />Traitement...</>
                        ) : (
                            <><i className="ri-check-line me-2"></i>Confirmer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .sortable-th-charte { cursor: pointer; user-select: none; transition: color 0.2s; }
                .sortable-th-charte:hover { color: var(--primary); }
                .table-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .table-link-charte:hover {
                    color: var(--primary-dark);
                    text-decoration: underline;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.9rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                }
                .mode-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    background: ${COLORS.primary}15;
                    display: flex; align-items: center; justify-content: center;
                    color: var(--primary); font-size: 1.1rem;
                    flex-shrink: 0;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-success, .btn-modern-danger {
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-primary:disabled,
                .btn-modern-outline:disabled,
                .btn-modern-success:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }
                .alert-info-charte {
                    border: none; background: #eff6ff;
                    border-left: 4px solid var(--info);
                    border-radius: 12px; padding: 1rem 1.25rem;
                    font-size: 0.85rem; color: #1e40af;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}